import { lazy } from 'react';

import { ICreateSendUnsetMskuState, TContentsBlockReason } from '../types/createSendContents';
import {
  CreateSendContentTypeEnum,
  CreateSendMethodEnum,
  CreateSendRecipientsModeEnum,
  ICreateSendStep,
  ICreateSendStepContext,
  ICreateSendStepDefinition,
  IStepBlockingAction,
  IStepFooterNote,
  IStepNavigation,
  TCreateSendMethod,
  TCreateSendStep,
} from '../types/createSendFlow';
import { TLinkSettingsBlockReason } from '../types/createSendLinkSettings';
import { TRecipientsBlockReason } from '../types/createSendRecipients';
import { TShipmentInfoBlockReason } from '../types/createSendShipmentInfo';
import { emailTokenBlockAction, requiredEmailTokenMessage } from './createSendEmail';
import {
  BUSINESS_REASON_BLOCK_ACTION,
  BUSINESS_REASON_BLOCK_MESSAGE,
  RECIPIENTS_BLOCK_MESSAGE,
  RELEASE_DATE_BLOCK_MESSAGE,
} from './createSendRecipients';
import { routes } from './routing';

export const EMAIL_OFF_FOOTER_NOTE: IStepFooterNote = {
  text: "Recipients won't be emailed",
  icon: 'mail-off',
};

export const CONTENTS_BLOCK_MESSAGE: Partial<Record<TContentsBlockReason, string>> = {
  'gift-budget-incomplete': 'Enter a gift budget amount, or turn the budget off',
  'empty-build': 'Add items to your kit — or give recipients a gift budget instead',
  'empty-curated': 'Select items — or give recipients a gift budget instead',
  'empty-location': 'Select at least one item',
  'out-of-stock': 'Replace the out-of-stock item to continue',
  'insufficient-stock': 'Reduce quantities to available stock',
  'out-of-stock-location': 'Remove out-of-stock items to continue',
  'insufficient-stock-location': 'Reduce quantities to available stock',
  'digital-location': 'Remove digital items to continue',
};

export const SHIPMENT_INFO_BLOCK_MESSAGE: Partial<Record<TShipmentInfoBlockReason, string>> = {
  'incomplete-contact': 'Enter contacts fields',
  'incomplete-destination': 'Enter the destination fields',
  'invalid-zip': 'Enter a valid ZIP code',
  'invalid-state': 'Enter a valid state or province',
  'business-reason-required': BUSINESS_REASON_BLOCK_MESSAGE,
  'org-settings-unknown': 'Checking your organization settings',
  'release-date-required': RELEASE_DATE_BLOCK_MESSAGE,
};

export const RECIPIENTS_BLOCK_ACTION: Partial<Record<TRecipientsBlockReason, IStepBlockingAction>> = {
  'business-reason-required': BUSINESS_REASON_BLOCK_ACTION,
};

export const SHIPMENT_INFO_BLOCK_ACTION: Partial<Record<TShipmentInfoBlockReason, IStepBlockingAction>> = {
  'business-reason-required': BUSINESS_REASON_BLOCK_ACTION,
};

export const LINK_SETTINGS_BLOCK_MESSAGE: Partial<Record<TLinkSettingsBlockReason, string>> = {
  'expiration-date-required': 'Expiration has no date',
  'on-hold-date-required': 'On hold has no date',
  'password-required': 'Password protection has no password',
  'domains-required': 'No allowed domains listed',
  'invalid-domain': 'One or more domains are invalid',
  'two-fa-recipients-required': 'Two-factor authentication has no recipients',
  'fixed-address-required': 'Enter the delivery address all gifts will ship to',
  'from-list-needs-2': 'Pick at least 2 addresses so the recipient has a real choice',
  'business-reason-required': BUSINESS_REASON_BLOCK_MESSAGE,
};

export const LINK_SETTINGS_BLOCK_ACTION: Partial<Record<TLinkSettingsBlockReason, IStepBlockingAction>> = {
  'expiration-date-required': { label: 'Pick a date', target: 'link-expiration' },
  'on-hold-date-required': { label: 'Pick a date', target: 'link-on-hold' },
  'password-required': { label: 'Enter a password', target: 'link-password' },
  'domains-required': { label: 'Add a domain', target: 'link-domains' },
  'invalid-domain': { label: 'Fix the domain', target: 'link-domains' },
  'two-fa-recipients-required': { label: 'Add recipients', target: 'link-two-fa-recipients' },
  'business-reason-required': BUSINESS_REASON_BLOCK_ACTION,
};

export const CONTINUE_ANYWAY_LABEL = 'Continue anyway';

export const getUnsetMskuNote = ({ count, firstName, isSendEmpty }: ICreateSendUnsetMskuState): string => {
  const subject = count === 1 ? firstName : `${count} kit items`;

  if (isSendEmpty) {
    const verb = count === 1 ? 'has' : 'have';

    return `Nothing to ship yet — ${subject} ${verb} no options assigned`;
  }

  return `${subject} won't be included — nothing assigned`;
};

const isUnsetMskuNoteVisible = (ctx: ICreateSendStepContext): boolean =>
  ctx.unsetMsku.count > 0 && (ctx.contents.reason === null || ctx.contents.reason === 'nothing-to-ship-location');

export const CREATE_SEND_ENTRY_NAV_LABEL = 'send setup';

export const CREATE_SEND_STEP_ORDER: TCreateSendStep[] = [
  'contents',
  'recipients',
  'link-settings',
  'shipment-info',
  'claim-page',
  'email',
  'custom-message',
  'review',
];

const alwaysApplicable = (): boolean => true;

const forMethod =
  (method: TCreateSendMethod) =>
  (ctx: ICreateSendStepContext): boolean =>
    ctx.method === method;

const ContentsStep = lazy(() => import('../containers/CreateSendContainer/Contents/ContentsStep'));
const RecipientsStep = lazy(() => import('../containers/CreateSendContainer/Recipients/RecipientsStep'));
const EmailStep = lazy(() => import('../containers/CreateSendContainer/Email/EmailStep'));
const ReviewStep = lazy(() => import('../containers/CreateSendContainer/Review/ReviewStep'));
const LinkSettingsStep = lazy(() => import('../containers/CreateSendContainer/LinkSettings/LinkSettingsStep'));
const ShipmentInfoStep = lazy(() => import('../containers/CreateSendContainer/ShipmentInfo/ShipmentInfoStep'));
const ClaimPageStep = lazy(() => import('../containers/CreateSendContainer/ClaimPage/ClaimPageStep'));
const CustomMessageStep = lazy(() => import('../containers/CreateSendContainer/CustomMessage/CustomMessageStep'));

export const CREATE_SEND_STEP_REGISTRY: Record<TCreateSendStep, ICreateSendStepDefinition> = {
  contents: {
    slug: 'contents',
    routeSlug: 'contents',
    navLabel: 'contents',
    component: ContentsStep,
    isApplicable: alwaysApplicable,
    validate: (ctx) => ctx.contents.valid,
    blockingMessage: (ctx) => (ctx.contents.reason ? CONTENTS_BLOCK_MESSAGE[ctx.contents.reason] : undefined),
    footerNote: (ctx) =>
      ctx.unsetMsku.attempted && isUnsetMskuNoteVisible(ctx)
        ? { text: getUnsetMskuNote(ctx.unsetMsku), icon: 'warning' }
        : undefined,
    ctaLabel: (ctx) =>
      ctx.unsetMsku.attempted && isUnsetMskuNoteVisible(ctx) && !ctx.unsetMsku.isSendEmpty
        ? CONTINUE_ANYWAY_LABEL
        : undefined,
    softBlock: (ctx) =>
      ctx.contents.reason === 'nothing-to-ship-location' && ctx.unsetMsku.count > 0 && !ctx.unsetMsku.attempted,
    continueGuard: (ctx) =>
      isUnsetMskuNoteVisible(ctx) && !ctx.unsetMsku.attempted && ctx.unsetMsku.key
        ? { key: ctx.unsetMsku.key }
        : undefined,
    prevOverride: routes.createSend.root,
  },
  recipients: {
    slug: 'recipients',
    routeSlug: 'recipients',
    navLabel: 'recipients',
    component: RecipientsStep,
    isApplicable: forMethod(CreateSendMethodEnum.Recipients),
    validate: (ctx) => ctx.recipients.valid,
    blockingMessage: (ctx) => (ctx.recipients.reason ? RECIPIENTS_BLOCK_MESSAGE[ctx.recipients.reason] : undefined),
    blockingAction: (ctx) => (ctx.recipients.reason ? RECIPIENTS_BLOCK_ACTION[ctx.recipients.reason] : undefined),
  },
  email: {
    slug: 'email',
    routeSlug: 'compose-email',
    navLabel: 'email',
    backLabel: 'email message',
    component: EmailStep,
    isApplicable: (ctx) =>
      (ctx.method === CreateSendMethodEnum.Recipients &&
        (ctx.recipientsMode === CreateSendRecipientsModeEnum.Many || ctx.isClaimBased || ctx.isContentDigital)) ||
      (ctx.method === CreateSendMethodEnum.Link &&
        ctx.isLinkPayloadDigital &&
        ctx.contentType !== CreateSendContentTypeEnum.Pyg),
    validate: (ctx) => ctx.validity.email,
    blockingMessage: (ctx) =>
      ctx.emailToken.isMissing ? requiredEmailTokenMessage(ctx.emailToken.required) : undefined,
    blockingAction: (ctx) => (ctx.emailToken.isMissing ? emailTokenBlockAction(ctx.emailToken.required) : undefined),
    blockingTone: (ctx) => (ctx.emailToken.isMissing ? 'error' : undefined),
    footerNote: (ctx) => (ctx.isEmailPreparing || ctx.emailNotificationsOn ? undefined : EMAIL_OFF_FOOTER_NOTE),
  },
  review: {
    slug: 'review',
    routeSlug: 'review',
    navLabel: 'review',
    component: ReviewStep,
    isApplicable: alwaysApplicable,
    validate: (ctx) => ctx.validity.review,
    blockingMessage: (ctx) => (ctx.validity.review ? undefined : RELEASE_DATE_BLOCK_MESSAGE),
  },
  'link-settings': {
    slug: 'link-settings',
    routeSlug: 'link-settings',
    navLabel: 'link settings',
    component: LinkSettingsStep,
    isApplicable: forMethod(CreateSendMethodEnum.Link),
    validate: (ctx) => ctx.validity.linkSettings,
    blockingMessage: (ctx) =>
      ctx.linkSettings.reason ? LINK_SETTINGS_BLOCK_MESSAGE[ctx.linkSettings.reason] : undefined,
    blockingAction: (ctx) =>
      ctx.linkSettings.reason ? LINK_SETTINGS_BLOCK_ACTION[ctx.linkSettings.reason] : undefined,
  },
  'shipment-info': {
    slug: 'shipment-info',
    routeSlug: 'shipment-info',
    navLabel: 'shipment info',
    component: ShipmentInfoStep,
    isApplicable: forMethod(CreateSendMethodEnum.Location),
    validate: (ctx) => ctx.shipmentInfo.valid,
    blockingMessage: (ctx) =>
      ctx.shipmentInfo.reason ? SHIPMENT_INFO_BLOCK_MESSAGE[ctx.shipmentInfo.reason] : undefined,
    blockingAction: (ctx) =>
      ctx.shipmentInfo.reason ? SHIPMENT_INFO_BLOCK_ACTION[ctx.shipmentInfo.reason] : undefined,
  },
  'claim-page': {
    slug: 'claim-page',
    routeSlug: 'claim-template',
    navLabel: 'claim page',
    component: ClaimPageStep,
    isApplicable: (ctx) =>
      ctx.method === CreateSendMethodEnum.Link || (ctx.method === CreateSendMethodEnum.Recipients && ctx.isClaimBased),
    validate: (ctx) => ctx.validity.claimPage,
  },
  'custom-message': {
    slug: 'custom-message',
    routeSlug: 'custom-message',
    navLabel: 'custom message',
    component: CustomMessageStep,
    isApplicable: (ctx) =>
      (ctx.method === CreateSendMethodEnum.Recipients || ctx.method === CreateSendMethodEnum.Link) &&
      !ctx.isContentDigital &&
      !ctx.isNotecardDisabled,
    validate: (ctx) => ctx.validity.customMessage,
  },
};

export const resolveCreateSendSteps = (ctx: ICreateSendStepContext): ICreateSendStep[] => {
  if (!ctx.method) {
    return [];
  }

  return CREATE_SEND_STEP_ORDER.map((slug) => CREATE_SEND_STEP_REGISTRY[slug])
    .filter((step) => step.isApplicable(ctx))
    .map((step) => ({
      slug: step.slug,
      routeSlug: step.routeSlug,
      navLabel: step.navLabel,
      backLabel: step.backLabel,
      valid: step.validate(ctx),
      blockingMessage: typeof step.blockingMessage === 'function' ? step.blockingMessage(ctx) : step.blockingMessage,
      blockingAction: step.blockingAction?.(ctx),
      blockingTone: step.blockingTone?.(ctx),
      footerNote: typeof step.footerNote === 'function' ? step.footerNote(ctx) : step.footerNote,
      ctaLabel: step.ctaLabel?.(ctx),
      softBlock: step.softBlock?.(ctx) ?? false,
      continueGuard: step.continueGuard?.(ctx),
      prevOverride: step.prevOverride,
    }));
};

export const getCreateSendStepUrl = (slug: string): string => routes.createSend.getUrl(slug);

export const resolveCreateSendStepNavigation = (
  steps: ReadonlyArray<Pick<ICreateSendStep, 'routeSlug' | 'navLabel' | 'backLabel' | 'prevOverride'>>,
  stepId: string | undefined,
): IStepNavigation => {
  const currentIndex = steps.findIndex((step) => step.routeSlug === stepId);
  const currentStep = currentIndex >= 0 ? steps[currentIndex] : undefined;
  const prevStep = currentIndex > 0 ? steps[currentIndex - 1] : undefined;
  const nextStep = currentIndex >= 0 && currentIndex < steps.length - 1 ? steps[currentIndex + 1] : undefined;

  const prev = currentStep?.prevOverride ?? (prevStep ? getCreateSendStepUrl(prevStep.routeSlug) : undefined);
  const next = nextStep ? getCreateSendStepUrl(nextStep.routeSlug) : undefined;

  const backLabel = prevStep
    ? prevStep.backLabel ?? prevStep.navLabel
    : currentStep?.prevOverride
    ? CREATE_SEND_ENTRY_NAV_LABEL
    : undefined;
  const continueToLabel = nextStep?.navLabel;

  return { currentIndex, prev, next, backLabel, continueToLabel };
};
